/**
 * Projection for `AgentsScene` — the demo's agent roster (`AG` at demo line
 * 2481, `AG_FILL` at 2498, `agLast` at 2513, `renderAgents` at 2514).
 *
 * ## The whole job of this file
 *
 * A card's "Last run" row is the only part of an agent that is not static.
 * `shared/seed/agents.ts` carries the fourteen `AG_FILL` closures as pure
 * functions of an explicit {@link AgentLastRunContext} precisely so that the
 * seed holds no numbers; assembling that context is what this file does.
 *
 * Seventeen fields, from four lazy domains, the eager register and the
 * narrative seeds. Two are worth explaining because they look like shortcuts
 * and are not:
 *
 * - **`assetRecords`** is the demo's `Object.keys(B.mx).length` — the size of
 *   the per-machine Maximo map. The port ships that map as one file per
 *   machine, one per register entry, so `FLEET.length` is the same figure
 *   without a hundred fetches.
 * - **`serialisedSubAssets`** is the demo's
 *   `Object.values(B.mx).reduce((a,m)=>a+(m.children||[]).length,0)`. Every
 *   Maximo child *is* a condition-register row — the extractor writes the two
 *   from the same source, and summing the hundred files gives 1,068, which is
 *   `HEALTH.length` exactly. Derived from the eager table for the same reason.
 *
 * `promises` and `upgradeCandidates` genuinely need Maximo asset records, so
 * the scene fetches the small set that can possibly contribute — see
 * {@link maximoMachinesNeeded}.
 *
 * Pure: no React, no fetching, no clock. Transformation layer (layer 3).
 *
 * @see src/versions/v1/modules/integration/AgentsScene.tsx
 * @see src/versions/v1/shared/seed/agents.ts
 * @see docs/logic/integration/agents.md
 */

import { FLEET, HEALTH, IDS } from '../../data/core';
import { ATTENTION_BANDS } from '../../schema/machine';
import type { MaximoAsset } from '../../schema/service';
import {
  draftWorkOrders,
  EXCHANGE,
  FLEET_RULES,
  healthBand,
  predictedUsage,
  promises,
  renewals,
  statusOf,
  upgradeCandidates,
  type CaseCtx,
  type ContractCtx,
  type InventoryCtx,
  type MaximoCtx,
  type OrderCtx,
  type PartsCatalogueCtx,
  type TechJobCtx,
  type WorkOrderCtx,
} from '../../shared/oem-engine';
import { ALERTS } from '../../shared/seed/alerts';
import {
  AGENTS,
  agentLastRunText,
  type AgentLastRunContext,
  type OemAgent,
} from '../../shared/seed/agents';
import { AGENT_COLOURS, MODULE_BY_ID } from '../../shared/seed/modules';

// ---------------------------------------------------------------------------
// Which Maximo records the page needs
// ---------------------------------------------------------------------------

/**
 * The machines whose Maximo asset record can change a figure on this page.
 *
 * `promises()` walks the seven open alerts; `upgradeCandidates()` walks the
 * whole register but discards anything outside an attention band before it
 * looks at the contract, so a machine in `good` or `fair` can never qualify.
 * The union of the two is about thirty records rather than a hundred, and the
 * figures are identical.
 */
export function maximoMachinesNeeded(): string[] {
  const ids = new Set<string>();
  for (const alert of ALERTS) ids.add(alert.machine);
  for (const f of FLEET) {
    if (ATTENTION_BANDS.includes(statusOf(f.machine_id))) ids.add(f.machine_id);
  }
  return [...ids].sort();
}

// ---------------------------------------------------------------------------
// The context
// ---------------------------------------------------------------------------

/** Everything the scene has loaded, in engine-context shape. */
export type AgentsSceneCtx = WorkOrderCtx &
  OrderCtx &
  PartsCatalogueCtx &
  InventoryCtx &
  CaseCtx &
  TechJobCtx &
  ContractCtx &
  MaximoCtx;

/** Demo line 2498 onwards. The seventeen fields, assembled once. */
export function agentLastRunContext(ctx: AgentsSceneCtx): AgentLastRunContext {
  const tally = promises(ctx);
  const nextRenewal = renewals(ctx)[0];

  return {
    openAlerts: ALERTS.length,
    openCases: ctx.cases.filter((c) => /Open/.test(c.status)).length,
    draftWorkOrders: draftWorkOrders(ctx).length,
    agentRequisitions: ctx.orders.filter((o) => /agent/i.test(o.status)).length,
    jobsInProgress: ctx.techJobs.filter((j) => j.status === 'In progress').length,
    fleetRules: FLEET_RULES.length,
    assetRecords: FLEET.length,
    serialisedSubAssets: HEALTH.length,
    flaggedComponents: HEALTH.filter((h) => ATTENTION_BANDS.includes(healthBand(h))).length,
    pmsScheduledAhead: ctx.workOrders.filter(
      (w) => w.wo_type === 'Preventive' && w.status === 'Scheduled' && w.created > '2026-09-09',
    ).length,
    minimumLevelProposals: predictedUsage(ctx).length,
    promises: tally.length
      ? { inside: tally.filter((p) => p.inside).length, total: tally.length }
      : null,
    exchangeUnits: EXCHANGE.length,
    contracts: ctx.contracts.length,
    upgradeCandidates: upgradeCandidates(ctx).length,
    nextRenewalDays: nextRenewal ? nextRenewal.days : 0,
    machineIds: IDS as AgentLastRunContext['machineIds'],
  };
}

/** Assemble {@link MaximoCtx} from the records the scene has fetched. */
export const maximoContext = (
  records: Readonly<Record<string, MaximoAsset | undefined>>,
): MaximoCtx => ({ maximo: records });

// ---------------------------------------------------------------------------
// The cards
// ---------------------------------------------------------------------------

/** One agent card, ready to render. */
export interface AgentCard {
  readonly agent: OemAgent;
  /** Module swatch — the demo's `AGENT_COL`, falling back to the accent. */
  readonly colour: string;
  /** `L2 Automated` / `L1 Assisted` — the demo's own pill text. */
  readonly levelLabel: string;
  /** The resolved "Last run" sentence, or `null` before the data lands. */
  readonly lastRun: string | null;
  /** `'04 Assess & Prescribe ›'` — the footer link, or `null`. */
  readonly moduleLabel: string | null;
  readonly moduleTo: string | null;
  /** Stable DOM id — the demo's `ag-<name with non-word chars stripped>`. */
  readonly domId: string;
}

/** Demo line 2514. `renderAgents` — the sixteen cards, in seed order. */
export function agentCards(ctx: AgentLastRunContext | null): AgentCard[] {
  return AGENTS.map((agent) => {
    const mod = MODULE_BY_ID[agent.module];
    return {
      agent,
      colour: AGENT_COLOURS[agent.name] ?? 'var(--accent)',
      levelLabel: agent.level === 'L2' ? 'L2 Automated' : 'L1 Assisted',
      lastRun: ctx ? agentLastRunText(agent, ctx) : null,
      moduleLabel: mod ? `${String(mod.n).padStart(2, '0')} ${mod.name}` : null,
      moduleTo: mod ? `/workflows/modules/${mod.id}` : null,
      domId: `ag-${agent.name.replace(/\W/g, '')}`,
    };
  });
}

/** Demo line 1453 — the header pill over the roster. */
export const agentCountChip = (): string =>
  `${AGENTS.length} agents · ${AGENTS.filter((a) => a.level === 'L2').length} at Level 2`;

// ---------------------------------------------------------------------------
// The two explainer cards
// ---------------------------------------------------------------------------

/** One rung of the demo's maturity ladder (demo line 1453). */
export interface MaturityRung {
  readonly level: string;
  readonly note: string;
  /** `true` for the two rungs the demo marks as in use today. */
  readonly active: boolean;
}

/** Demo line 1453 — `.card.matur`, verbatim. */
export const MATURITY: readonly MaturityRung[] = [
  {
    level: 'L1 · Assisted',
    note: 'Insights only. — Kalbarri (joined pilot 08 Sep)',
    active: true,
  },
  {
    level: 'L2 · Automated',
    note: 'Drafts WOs, requisitions, restrictions for approval. — Broken Ridge, Mount Isolde',
    active: true,
  },
  {
    level: 'L3 · Autonomous with oversight',
    note: 'Executes pre-approved low-risk actions. Not enabled.',
    active: false,
  },
  {
    level: 'L4 · Self-optimising fleet',
    note: 'Proposes policy changes, review by exception. Not enabled.',
    active: false,
  },
];

/**
 * How the assistant actually runs today.
 *
 * The demo's own sentence (demo line 1453) is the first row; the second is the
 * port's, and it is the honest one — the answer engine in
 * `shared/assistant/local-answer.ts` is deterministic and local, and a live
 * Claude call through a Cloudflare Worker is a roadmap row, not a feature.
 */
export interface RuntimeNote {
  readonly heading: string;
  readonly body: string;
}

export const RUNTIME_NOTES: readonly RuntimeNote[] = [
  {
    heading: 'One assistant, sixteen specialists',
    body:
      "The assistant answers by calling the workspace's own query functions over the embedded " +
      'data and shows which queries it ran. Each agent below is one of those specialists: a ' +
      'defined input, a defined output, and a point where a person approves.',
  },
  {
    heading: 'Deterministic, and local',
    body:
      'Every answer in this proof of concept is produced by a local answer engine reading the ' +
      'same tables the pages read. Nothing leaves the browser and nothing is generated, so the ' +
      'same question gives the same answer every time — which is what makes the source chips ' +
      'under an answer checkable.',
  },
  {
    heading: 'Live models are the next step',
    body:
      'The ten tool contracts are already written as data. Pointing them at a live Claude call ' +
      'through a Cloudflare Worker is a backend phase; the agent contracts on these cards do ' +
      'not change when it lands.',
  },
];
